import React, { useEffect, useMemo, useState } from "react";
import {
  DataTable,
  rfqListConfig,
  DataToolBar,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@b2b/ui-components";
import { Loader2, MoreHorizontal, XCircle, X, Eye, Edit } from "lucide-react";
import { rfqService } from "@b2b/services";
import { useNavigate, Link } from "react-router-dom";

// Types
interface RfqRow {
  id: string;
  title: string;
  category: string;
  buyerCompany: string;
  country: string;
  quantity: number;
  unit: string;
  createdAt: string;
  dueDate: string;
  status: "Open" | "Pending" | "Closed" | string;
}

interface ToolbarState {
  q: string;
  status: string;
  country: string;
  categoryId?: string;
  from?: Date;
  to?: Date;
  pageSize: number;
}

interface RfqListProps {
  jsonUrl?: string;
  onCreateRfq?: () => void;
  onAction?: (id: string, action: string) => void;
}

interface ToastState {
  type: "success" | "error";
  message: string;
}

const seedData: RfqRow[] = [];
const defaultToolbar: ToolbarState = {
  q: "",
  status: "all",
  country: "all",
  from: undefined,
  to: undefined,
  pageSize: 10,
};

function formatDate(dateStr: string) {
  const date = new Date(dateStr);
  return isNaN(date.getTime()) ? "-" : date.toLocaleDateString();
}

const RfqListPage: React.FC<RfqListProps> = ({
  jsonUrl,
  onCreateRfq,
  onAction,
}) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<RfqRow[]>(seedData);
  const [toolbar, setToolbar] = useState<ToolbarState>(defaultToolbar);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [toast, setToast] = useState<ToastState | null>(null);

  const showToast = (type: "success" | "error", message: string) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 5000);
  };

  // Fetch data from API
  useEffect(() => {
    let active = true;
    
    const fetchRfqs = async () => {
      setLoading(true);
      try {
        const response = await rfqService.getRfqList({
          page: currentPage,
          limit: toolbar.pageSize,
          search: toolbar.q || undefined,
          status: toolbar.status !== "all" ? toolbar.status as any : undefined,
          categoryId: toolbar.categoryId || undefined,
          sortBy: "createdAt",
          sortOrder: "desc",
        });
        
        if (!active) return;
        
        // Check if response and items exist
        if (!response || !response.items || !Array.isArray(response.items)) {
          console.warn("Invalid response structure:", response);
          setRows([]);
          setTotalPages(1);
          return;
        }
        
        // Map API response to RfqRow format
        const mappedRows: RfqRow[] = response.items.map((rfq) => ({
          id: rfq.id,
          title: rfq.title,
          category: rfq.categories?.[0]?.name || rfq.categoryIds?.[0] || "N/A",
          buyerCompany: rfq.buyer?.company?.name || "N/A",
          country: rfq.buyer?.company?.name || "N/A", // Map from buyer data if available
          quantity: rfq.quantity,
          unit: rfq.unit,
          createdAt: rfq.createdAt,
          dueDate: rfq.preferredDeliveryDate || rfq.expiryDate || "",
          status: rfq.status,
        }));
        
        setRows(mappedRows);
        setTotalPages(response.totalPages || 1);
      } catch (error: any) {
        console.error("Error fetching RFQs:", error);
        showToast("error", error?.response?.data?.message || "Failed to load RFQs");
      } finally {
        if (active) setLoading(false);
      }
    };

    fetchRfqs();

    return () => {
      active = false;
    };
  }, [currentPage, toolbar.pageSize, toolbar.q, toolbar.status]);

  const countries = useMemo(
    () => Array.from(new Set(rows.map((d) => d.country))).sort(),
    [rows]
  );

  function setToolbarPartial(p: Partial<ToolbarState>) {
    setToolbar((s) => ({ ...s, ...p }));
  }

  function resetFilters() {
    setToolbar(defaultToolbar);
  }

  function exportCsv() {
    const header = [
      "id",
      "title",
      "category",
      "buyerCompany",
      "country",
      "quantity",
      "unit",
      "createdAt",
      "dueDate",
      "status",
    ];
    const rowsData = filtered.map((r) => [
      r.id,
      r.title,
      r.category,
      r.buyerCompany,
      r.country,
      String(r.quantity),
      r.unit,
      r.createdAt,
      r.dueDate,
      r.status,
    ]);
    const csv = [
      header.join(","),
      ...rowsData.map((x) =>
        x.map((y) => `"${String(y).replaceAll('"', '""')}"`).join(",")
      ),
    ].join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `rfqs_${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const filtered = useMemo(() => {
    const q = toolbar.q.toLowerCase().trim();
    const fromMs = toolbar.from?.getTime();
    const toMs = toolbar.to?.getTime();

    return rows.filter((r) => {
      if (toolbar.status !== "all" && r.status !== toolbar.status) return false;
      if (toolbar.country !== "all" && r.country !== toolbar.country)
        return false;
      if (fromMs && new Date(r.createdAt).getTime() < fromMs) return false;
      if (toMs && new Date(r.createdAt).getTime() > toMs) return false;

      if (q) {
        const hay = `${r.id} ${r.title} ${r.buyerCompany} ${r.category} ${r.country}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rows, toolbar]);

  const tableData = filtered.map((r) => ({
    id: r.id,
    title: (
      <div className="flex min-w-0 flex-col">
        <span className="truncate font-medium">{r.title}</span>
        <span className="text-xs text-muted-foreground lg:hidden">
          {r.buyerCompany}
        </span>
      </div>
    ),
    category: r.category,
    buyerCompany: r.buyerCompany,
    country: r.country,
    quantity: `${r.quantity.toLocaleString()} ${r.unit}`,
    createdAt: formatDate(r.createdAt),
    dueDate: formatDate(r.dueDate),
    status: r.status,
    actions: (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            size="sm"
            variant="ghost"
            className="h-8 w-8 p-0"
          >
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem asChild>
            <Link to={`/buyer/rfq/${r.id}`}>
              <Eye className="mr-2 h-4 w-4" />
              View Details
            </Link>
          </DropdownMenuItem>
          {r.status === "DRAFT" && (
            <DropdownMenuItem asChild>
              <Link to={`/buyer/rfq/${r.id}/edit`}>
                <Edit className="mr-2 h-4 w-4" />
                Edit RFQ
              </Link>
            </DropdownMenuItem>
          )}
          {(r.status === "OPEN" || r.status === "DRAFT") && (
            <DropdownMenuItem
              onClick={async () => {
                if (window.confirm("Are you sure you want to cancel this RFQ? This action cannot be undone.")) {
                  try {
                    setLoading(true);
                    await rfqService.cancelRfq(r.id);
                    showToast("success", "RFQ cancelled successfully");
                    // Refresh the list
                    const response = await rfqService.getRfqList({
                      page: currentPage,
                      limit: toolbar.pageSize,
                      search: toolbar.q || undefined,
                      status: toolbar.status !== "all" ? toolbar.status as any : undefined,
                      categoryId: toolbar.categoryId || undefined,
                      sortBy: "createdAt",
                      sortOrder: "desc",
                    });
                    if (response && response.items) {
                      const mappedRows: RfqRow[] = response.items.map((rfq: any) => ({
                        id: rfq.id,
                        title: rfq.title,
                        category: rfq.categories?.[0]?.name || rfq.categoryIds?.[0] || "N/A",
                        buyerCompany: rfq.buyer?.company?.name || "N/A",
                        country: rfq.buyer?.company?.name || "N/A",
                        quantity: rfq.quantity,
                        unit: rfq.unit,
                        createdAt: rfq.createdAt,
                        dueDate: rfq.preferredDeliveryDate || rfq.expiryDate || "",
                        status: rfq.status,
                      }));
                      setRows(mappedRows);
                      setTotalPages(response.totalPages || 1);
                    }
                  } catch (error: any) {
                    console.error("Error cancelling RFQ:", error);
                    showToast("error", error?.response?.data?.message || "Failed to cancel RFQ");
                  } finally {
                    setLoading(false);
                  }
                }
              }}
            >
              <XCircle className="mr-2 h-4 w-4" />
              Cancel RFQ
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    ),
  }));

  return (
    <div className="min-h-screen">
      {/* Toast Notification */}
      {toast && (
        <div
          className={`fixed top-4 right-4 z-50 flex items-center gap-2 p-4 rounded-lg shadow-lg max-w-[400px] ${
            toast.type === "success"
              ? "bg-green-500 text-white"
              : "bg-red-500 text-white"
          }`}
        >
          <XCircle className="w-5 h-5 flex-shrink-0" />
          <p className="flex-1 font-medium text-sm">{toast.message}</p>
          <button
            onClick={() => setToast(null)}
            className="ml-2 hover:opacity-80 transition-opacity flex-shrink-0"
            aria-label="Close notification"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <main className="flex-1 p-6 max-w-[1400px] mx-auto w-full">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-2xl font-semibold">RFQ List</h2>
            <p className="text-sm text-gray-500">
              Browse, filter, and manage requests for quotation.
            </p>
          </div>
          {loading && (
            <div className="flex items-center text-sm text-gray-500">
              <Loader2 className="h-3.5 w-3.5 animate-spin mr-2" /> Loading...
            </div>
          )}
        </div>

        <DataToolBar
          state={toolbar}
          setState={setToolbarPartial}
          countries={countries}
          onExport={exportCsv}
          onReset={resetFilters}
          onCreate={() => onCreateRfq?.()}
        />

        <hr className="my-4 border-t border-gray-300" />

        <DataTable
          data={tableData}
          config={rfqListConfig}
          rowsPerPage={toolbar.pageSize}
        />

        <p className="mt-6 text-xs text-gray-500">
          Tip: Use the search to find by ID, title, or company. Export applies
          to the filtered list.
        </p>
      </main>
    </div>
  );
};

export default RfqListPage;
